import type { DecodedJwt, DecodedJwtPayload } from "@/types/token";

function base64UrlDecode(segment: string): string {
  const padding = "=".repeat((4 - (segment.length % 4)) % 4);
  const base64 = (segment + padding).replace(/-/g, "+").replace(/_/g, "/");
  const binary = atob(base64);
  // Decode as UTF-8 – tokens often contain non-ASCII characters.
  const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));
  return new TextDecoder().decode(bytes);
}

/**
 * Safely decode a JWT without verifying the signature. Returns `null`
 * if the token is clearly not a JWT (e.g. an opaque access token from
 * Microsoft Graph, which is NOT a JWT).
 */
export function decodeJwt(raw: string | null | undefined): DecodedJwt | null {
  if (!raw) return null;
  const parts = raw.split(".");
  if (parts.length !== 3) return null;
  try {
    const header = JSON.parse(base64UrlDecode(parts[0])) as Record<
      string,
      unknown
    >;
    const payload = JSON.parse(base64UrlDecode(parts[1])) as DecodedJwtPayload;
    return { header, payload, signature: parts[2], raw };
  } catch {
    return null;
  }
}

export function toStringArray(value: unknown): string[] {
  if (Array.isArray(value)) return value.filter((v): v is string => typeof v === "string");
  if (typeof value === "string") return [value];
  return [];
}

export function extractRoles(payload: DecodedJwtPayload | null): string[] {
  if (!payload) return [];
  return toStringArray(payload.roles);
}

export function extractGroups(payload: DecodedJwtPayload | null): string[] {
  if (!payload) return [];
  return toStringArray(payload.groups);
}

export function extractScopes(payload: DecodedJwtPayload | null): string[] {
  if (!payload) return [];
  if (typeof payload.scp === "string") return payload.scp.split(/\s+/).filter(Boolean);
  return toStringArray((payload as Record<string, unknown>).scope);
}
